import React from 'react';
import { IAssistantMessage, IQuestionOption, QuestionType } from '../types';
import { AlertMessageBubble } from './message-bubles/alert';
import { ChatMessage } from './message-box';
import { FormMessageBubles } from './message-bubles/form';
import { SelectMultipleMessageBubles } from './message-bubles/multiple-choice';
import { SelectSingleMessageBubles } from './message-bubles/single-select';
import { TextInputMessageBubles } from './message-bubles/text-input';
import { UserMessageBubble } from './message-bubles/user-message-bubble';
import { PriceSurgeMessageBubble } from './message-bubles/price-surge';
import { TypingIndicator } from './TypingIndicator';
import { Obj } from '@/types';
import {
    extractDimensionsFromMessages,
    OPTION_TYPE_NUMERIC_VALUES,
    ExtractedDimensions,
    FireEgressWindowType,
} from '../utils/fireEgress';
import { FieldConstraint } from './message-bubles/form/FormCard';
import { DIMENSION_OPTION, OPTION_TYPE_CUSTOM_FRACTION, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE, PROFILE_COLOR_OPTION, ProductStructureType } from '../constants';
import { getFormValuesFromMessages, resolveVentWidth } from '../utils/displayCondition';
import { getArchProfileColourWidthAlert, getSweptArchProfileColourAlert, isSweptArchProduct } from '../utils/profileColour';

const OPTION_TYPE_CILL = 15;
const CILL_225_RESPONSE_VALUES = ['225mm_-_white', '225_cill_-_coloured'];

function is225CillSelected(previousMessages: IAssistantMessage[]): boolean {
    for (const msg of previousMessages) {
        if (msg.question?.optionType === OPTION_TYPE_CILL && (msg.response?.answer?.length ?? 0) > 0) {
            return (msg.response?.answer ?? []).some(opt =>
                CILL_225_RESPONSE_VALUES.includes(opt.responseValue)
            );
        }
    }
    return false;
}

function getAngleFieldConstraints(
    options: IQuestionOption[] | undefined,
    previousMessages: IAssistantMessage[]
): Record<string, FieldConstraint> | undefined {
    const angleOptions = options?.filter(opt => /^angle_\d+_\(°\)$/.test(opt.responseValue ?? ''));
    if (!angleOptions?.length) return undefined;
    const allow160 = is225CillSelected(previousMessages);
    const constraints: Record<string, FieldConstraint> = {};
    angleOptions.forEach(opt => {
        constraints[opt.responseValue] = { allow160 };
    });
    return constraints;
}



interface ChatMessagesProps {
    messages: IAssistantMessage[];
    selectedProduct?: IQuestionOption;
    /** Full conversation history used only for fire-egress extraction in edit mode.
     *  In normal mode this is omitted and messages.slice(0, index) is used instead. */
    contextMessages?: IAssistantMessage[];
    validationRules?: any[];
    isEditMode?: boolean; // Flag to indicate edit mode (disables confirmation modals)

    // Fire egress consent
    egressConsentState?: null | 'pending' | 'accepted' | 'rejected';
    onEgressConsentAccept?: () => void;
    onEgressConsentReject?: () => void;

    // Single select methods
    handleSingleSelect: (questionId: string, option: IQuestionOption) => void;
    updateSingleSelect: (questionId: string, option: IQuestionOption) => void;
    // Any blend stage (DBR/colorize/grill/final AI blend) still running —
    // disables the "Add to Cart" option specifically so the chat can't
    // advance past it with a stale/incomplete image.
    isBlending?: boolean;

    // Multiple choice methods
    handleMultipleChoice: (questionId: string, options: IQuestionOption[]) => void;
    updateMultipleChoice: (questionId: string, options: IQuestionOption[]) => void;

    // Form methods
    handleFormSubmit: (questionId: string, formValues: Obj) => void;
    updateFormSubmit: (questionId: string, formValues: Obj) => void;

    // Text input methods
    handleTextInput: (questionId: string, textValue: string) => void;
    updateTextInput: (questionId: string, textValue: string) => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
    messages,
    selectedProduct,
    contextMessages,
    validationRules = [],
    isEditMode = false,
    egressConsentState,
    onEgressConsentAccept,
    onEgressConsentReject,
    handleSingleSelect,
    updateSingleSelect,
    isBlending = false,
    handleMultipleChoice,
    updateMultipleChoice,
    handleFormSubmit,
    updateFormSubmit,
    handleTextInput,
    updateTextInput,
}) => {
    // Check if there's a processing message
    const hasProcessingMessage = messages.some(msg => msg.isProcessing);

    const renderMessage = (message: IAssistantMessage, index: number) => {
        const messageId = message.id || `temp_${Math.random()}`;

        // In edit mode contextMessages holds the full merged history (original + draft).
        // Use it for egress extraction so dimension answers from earlier in the
        // conversation are found even though draftMessages only contains the edited question.
        const previousMessages = contextMessages
            ? contextMessages.filter(
                (m) => m.id !== message.id &&
                    (m.response?.form && Object.keys(m.response.form ?? {}).length > 0 ||
                     (m.response?.answer?.length ?? 0) > 0)
              )
            : messages.slice(0, index);

        // User messages
        if (message.type === 'user') {
            return <UserMessageBubble key={messageId} message={message} />;
        }

        // Price surge messages
        if (message.type === 'price-surge') {
            return <PriceSurgeMessageBubble key={messageId} message={message} />;
        }

        // Generic alert messages
        if (message.type === 'alert') {
            const hasEgressActions = message.alertActions?.some(a =>
                a.actionId === 'egress_accept' || a.actionId === 'egress_reject'
            );
            const actionHandler = hasEgressActions && egressConsentState === 'pending'
                ? (actionId: string) => {
                    if (actionId === 'egress_accept') onEgressConsentAccept?.();
                    if (actionId === 'egress_reject') onEgressConsentReject?.();
                }
                : undefined;
            return <AlertMessageBubble key={messageId} message={message} onAction={actionHandler} />;
        }

        // Bot messages with questions
        if (message.type === 'assistant') {
            // Processing message (typing indicator)
            if (message.isProcessing) {
                return <TypingIndicator key={messageId} />;
            }

            const questionType = message.question?.questionType;

            const isNumericValues = message.question?.optionType === OPTION_TYPE_NUMERIC_VALUES;
            const isCustomFraction = message.question?.optionType === OPTION_TYPE_CUSTOM_FRACTION;

            let egressDimensions: ExtractedDimensions | undefined;
            let egressWindowType: FireEgressWindowType | undefined;

            if (isNumericValues || isCustomFraction) {
                egressDimensions = extractDimensionsFromMessages(previousMessages);
                const ptc = selectedProduct?.producttypecode;
                egressWindowType = ptc === ProductStructureType.Coupled ? 'couple'
                    : ptc === ProductStructureType.Bays ? 'bay'
                        : ptc === ProductStructureType.Arch ? 'arch'
                            : 'standard';
            }

            switch (questionType) {
                case QuestionType.SINGLE_SELECT:
                case QuestionType.SINGLE_SELECT_WITH_IMAGE:
                case QuestionType.BOOLEAN:
                case QuestionType.SWATCH_SELECT:
                case QuestionType.VISUAL_SELECT: {
                    

                    let archProfileColourAlert: string | null = null;
                    if (message.question?.optionType === PROFILE_COLOR_OPTION &&
                        selectedProduct?.producttypecode === ProductStructureType.Arch) {
                        const formValues = getFormValuesFromMessages(previousMessages);
                        const width = resolveVentWidth(formValues);
                        if (isSweptArchProduct(selectedProduct.optionText)) {
                            const overallHeight = Number(formValues['overall_height_(mm)'] ?? 0);
                            const shoulderHeight = Number(formValues['shoulder_height_(mm)'] ?? 0);
                            archProfileColourAlert = getSweptArchProfileColourAlert(width, overallHeight, shoulderHeight);
                        } else {
                            archProfileColourAlert = getArchProfileColourWidthAlert(width);
                        }
                    }

                    return (
                        <SelectSingleMessageBubles
                            key={messageId}
                            message={message}
                            handleSelect={handleSingleSelect}
                            updateSelect={updateSingleSelect}
                            isProcessing={hasProcessingMessage}
                            isEditMode={isEditMode}
                            alertMessage={archProfileColourAlert}
                            isBlending={isBlending}
                        />
                    );
                }

                case QuestionType.MULTIPLE_CHOICE: {
                    
                    return (
                        <SelectMultipleMessageBubles
                            key={messageId}
                            message={message}
                            handleSelect={handleMultipleChoice}
                            updateSelect={updateMultipleChoice}
                            isProcessing={hasProcessingMessage}
                            isEditMode={isEditMode}
                        />
                    );
                }

                case QuestionType.TEXT_INPUT:
                case QuestionType.NUMBER_INPUT:
                    // Check if it's a form (multiple fields) or single text input
                    if (message.question?.options && message.question.options.length > 0) {
                        const isDimensionForm = message.question?.optionType === DIMENSION_OPTION;

                        const angleConstraints = isDimensionForm
                            ? getAngleFieldConstraints(message.question?.options, previousMessages)
                            : undefined;

                        const effectiveFieldConstraints = angleConstraints;

                        const isCoupleDimension = isDimensionForm &&
                            (selectedProduct?.producttypecode === ProductStructureType.Coupled ||
                             selectedProduct?.producttypecode === ProductStructureType.Bays);

                        const isTrueArch = isDimensionForm &&
                            selectedProduct?.producttypecode === ProductStructureType.Arch &&
                            !isSweptArchProduct(selectedProduct?.optionText);

                        return (
                            <FormMessageBubles
                                validationRules={validationRules}
                                key={messageId}
                                message={message}
                                handleSubmit={handleFormSubmit}
                                updateSubmit={updateFormSubmit}
                                isEditMode={isEditMode}
                                egressDimensions={(isNumericValues || isCustomFraction) ? egressDimensions : undefined}
                                egressWindowType={(isNumericValues || isCustomFraction) ? egressWindowType : undefined}
                                fieldConstraints={effectiveFieldConstraints}
                                isCoupleDimension={isCoupleDimension}
                                isTrueArch={isTrueArch}
                            />
                        );
                    } else {
                        return (
                            <TextInputMessageBubles
                                key={messageId}
                                message={message}
                                handleSubmit={handleTextInput}
                                updateSubmit={updateTextInput}
                                isProcessing={hasProcessingMessage}
                            />
                        );
                    }


                default:
                    // Fallback to basic chat message
                    return <ChatMessage key={messageId} message={message} />;
            }
        }

        return null;
    };

    return (
        <div className="space-y-4">
            {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                    <p className="text-neutral-500 text-center">
                        Start a conversation with the assistant
                    </p>
                </div>
            ) : (
                messages.map((message, index) => renderMessage(message, index))
            )}
        </div>
    );
};
